// The rooms (Apps/Shared/Rooms/Room.swift): Plan · Research · Write · Revise · Publish in the order a book is worked
// on, plus the Library behind the project's name. Plan holds the story pages (cast, timeline, chronology, continuity,
// shape, glossary) after its boards; Research is the editor over Research & Notes, with a selection of its own
// (ProjectShell `sideOf`). Each room's pages are listed here in the Mac's order (`RoomPage`), with the tab's name, its
// Help article and the column it brings; the registry (registry.ts) fills each with the page a file under
// rooms/<room>/ registers, and a placeholder until one does. Pages only the web has (Revise ▸ Readers, Agents on the
// web) are registered by their files alone.

export type RoomId = 'plan' | 'research' | 'write' | 'revise' | 'publish' | 'library';

/** Which column a room shows beside its pages when a page brings none of its own. */
export type ColumnKind = 'contents' | 'projects';

/** Whether a room's contents are the project's reference arrangements (Research & Notes) rather than the manuscript's side. */
export const showsResearch = (room: RoomId) => room === 'research';

/** Plan lays out either side: its boards and outline show whichever arrangement they're given (Research & Notes'
 *  Characters folder as cast cards), so its side is its arrangement's. */
export const showsEitherSide = (room: RoomId) => room === 'plan';

export interface CanonicalPage {
  page: string;
  title: string;
  /** The Help article (Apps/Shared/Help/<help>.md). */
  help?: string;
  /** The page is about an item in the contents (the URL carries the node, the inspector follows it). */
  takesNode?: boolean;
  /** Gathered under one tab with a menu (Plan's story pages under "Story"), so the room's tabs stay few. */
  group?: string;
}

export interface RoomDef {
  id: RoomId;
  title: string;
  /** ⌥⌘<key> (Ctrl+Alt+<key> elsewhere). */
  key: string;
  column: ColumnKind;
  /** The name of the room's own column (the phone's sheet title), when it isn't the contents. */
  columnTitle?: string;
  help: string;
  pages: CanonicalPage[];
}

export const ROOMS: readonly RoomDef[] = [
  {
    id: 'plan',
    title: 'Plan',
    key: '1',
    column: 'contents',
    help: 'corkboard',
    pages: [
      { page: 'corkboard', title: 'Corkboard', help: 'corkboard', takesNode: true },
      { page: 'outliner', title: 'Outliner', help: 'outliner', takesNode: true },
      { page: 'beats', title: 'Beats', help: 'beats' },
      { page: 'maps', title: 'Mind Maps', help: 'mind-maps' },
      { page: 'cast', title: 'Cast & Places', help: 'story-view', group: 'Story' },
      { page: 'study', title: 'Character study', help: 'character-studies', group: 'Story' },
      { page: 'timeline', title: 'Timeline', help: 'story-view', group: 'Story' },
      { page: 'chronology', title: 'Chronology', help: 'story-view', group: 'Story' },
      { page: 'continuity', title: 'Continuity', help: 'continuity', group: 'Story' },
      { page: 'shape', title: 'Shape', help: 'shape', group: 'Story' },
      { page: 'glossary', title: 'Glossary', help: 'glossary', group: 'Story' },
    ],
  },
  {
    id: 'research',
    title: 'Research',
    key: '2',
    column: 'contents',
    help: 'research',
    pages: [{ page: 'research', title: 'Research', help: 'research', takesNode: true }],
  },
  {
    id: 'write',
    title: 'Write',
    key: '3',
    column: 'contents',
    help: 'scrivenings',
    pages: [{ page: 'write', title: 'Write', help: 'scrivenings', takesNode: true }],
  },
  {
    id: 'revise',
    title: 'Revise',
    key: '4',
    column: 'contents',
    help: 'style',
    pages: [
      { page: 'checks', title: 'Checks', help: 'style' },
      { page: 'comments', title: 'Comments', help: 'margin-comments' },
      { page: 'history', title: 'History', help: 'checkpoints' },
    ],
  },
  {
    id: 'publish',
    title: 'Publish',
    key: '5',
    column: 'contents',
    help: 'compile',
    pages: [{ page: 'publish', title: 'Publish', help: 'compile' }],
  },
  {
    id: 'library',
    title: 'Library',
    key: '0',
    column: 'projects',
    columnTitle: 'Projects',
    help: 'rooms',
    pages: [
      { page: 'home', title: 'Home', help: 'rooms' },
      { page: 'statistics', title: 'All statistics', help: 'statistics' },
    ],
  },
];

/** The five across the top (the Library opens from the project's name, or ⌥⌘0). */
export const WORKING_ROOMS: readonly RoomId[] = ['plan', 'research', 'write', 'revise', 'publish'];

export const isRoom = (s: string): s is RoomId => ROOMS.some((r) => r.id === s);

export function roomDef(id: RoomId): RoomDef {
  return ROOMS.find((r) => r.id === id)!;
}
